import { type IHttpClient } from '@wix/yoshi-flow-editor';
import { get, set, unset } from 'lodash';
import { getAdUnit } from '@wix/ambassador-adsense-v1-adsense-adunit/http';
import {
  type AdUnit,
  type GetAdUnitResponse,
} from '@wix/ambassador-adsense-v1-adsense-adunit/types';
import { updateUser } from '@wix/ambassador-adsense-v1-adsense-user/http';
import {
  type UpdateUserResponse,
  type User,
} from '@wix/ambassador-adsense-v1-adsense-user/types';
import { findAdUnitId } from '@app/utils';

export const fetchAdUnitByCompId = async (
  httpClient: IHttpClient,
  user: User,
  instanceId: string,
  originCompId: string,
): Promise<AdUnit | undefined> => {
  try {
    if (!user) {
      return;
    }

    const adUnitId = findAdUnitId(
      originCompId,
      user.adsenseInfo?.connectedAdunits,
    );

    if (adUnitId) {
      const { data } = await httpClient.request<GetAdUnitResponse>(
        getAdUnit({ instanceId, reportingDimensionId: adUnitId }),
      );
      return data?.adUnit;
    }
  } catch (error) {
    console.error('Error fetching ad unit:', error);
  }
};

export async function toggleConnectedAdUnit(
  selectedAdUnitId: string | undefined,
  currentConnectedAdUnitId: string | undefined,
  prevUser: User,
  compId: string,
  instanceId: string,
  httpClient: any,
): Promise<UpdateUserResponse> {
  const user: User = structuredClone(prevUser); // Avoid mutation issues
  user.adsenseInfo ??= { connectedAdunits: {} };
  user.adsenseInfo.connectedAdunits ??= {};

  if (currentConnectedAdUnitId) {
    user.adsenseInfo.connectedAdunits[currentConnectedAdUnitId] = get(
      user.adsenseInfo.connectedAdunits,
      currentConnectedAdUnitId,
      { compId: [] },
    );

    const setPath = `adsenseInfo.connectedAdunits.${currentConnectedAdUnitId}.compId`;
    const unsetPath = `adsenseInfo.connectedAdunits.${currentConnectedAdUnitId}`;

    const updatedCompIds = get(user, setPath, []).filter(
      (id: string) => id !== compId,
    );
    if (updatedCompIds.length > 0) {
      set(user, setPath, updatedCompIds);
    } else {
      unset(user, unsetPath);
    }
  }

  if (selectedAdUnitId) {
    user.adsenseInfo.connectedAdunits[selectedAdUnitId] = get(
      user.adsenseInfo.connectedAdunits,
      selectedAdUnitId,
      { compId: [] },
    );

    const getPath = `adsenseInfo.connectedAdunits.${selectedAdUnitId}.compId`;
    const currentCompIds = get(user, getPath, []) as string[];
    if (!currentCompIds.includes(compId)) {
      set(user, getPath, [...currentCompIds, compId]);
    }
  }

  const { data } = await httpClient.request(
    updateUser({ instanceId, user: { adsenseInfo: user.adsenseInfo } }),
  );

  return data;
}
